import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AdminProvider, useAdmin } from './workflows/AdminData'
import GroupsTab from './workflows/GroupsTab'
import RoutingTab from './workflows/RoutingTab'
import WorkflowsTab from './workflows/WorkflowsTab'

export type AdminTab = 'workflows' | 'groups' | 'routing'

const COPY: Record<AdminTab, { title: string; description: string }> = {
    workflows: { title: 'Workflows', description: 'Define who approves a request, and in what order.' },
    groups: { title: 'Groups', description: 'Named sets of people that workflows can send requests to.' },
    routing: { title: 'Routing', description: 'Choose which workflow handles each form or kind of request.' },
}

function Body({ tab }: { tab: AdminTab }) {
    const { error } = useAdmin()
    return (
        <>
            {error && (
                <Alert variant="destructive"><AlertCircle /><AlertTitle>Could not load admin data</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
            )}
            {tab === 'workflows' && <WorkflowsTab />}
            {tab === 'groups' && <GroupsTab />}
            {tab === 'routing' && <RoutingTab />}
        </>
    )
}

/** One of the workflow administration pages; the Manage navigation switches between them. */
export default function WorkflowsAdminPage({ tab }: { tab: AdminTab }) {
    const navigate = useNavigate()
    const goTab = useCallback((t: AdminTab) => void navigate({ to: `/manage/${t}` }), [navigate])
    return (
        <div className="flex flex-col gap-6">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">{COPY[tab].title}</h1>
                <p className="text-sm text-muted-foreground">{COPY[tab].description}</p>
            </div>
            <AdminProvider goTab={goTab}>
                <Body tab={tab} />
            </AdminProvider>
        </div>
    )
}
